import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, Calendar, Settings, Filter, Users, Trophy,
  DollarSign, ChevronDown, ChevronRight, Search, FileText, Clock
} from 'lucide-react';
import ActivityEnrollModal from './modals/ActivityEnrollModal';
import ActivityModal from './modals/ActivityModal';
import ActivityReportModal from './modals/ActivityReportModal';
import { Gate } from './AccessStudio';

const Activities = ({ apiCall, selectedYear, hasRole, hasPermission, showSuccess, showError }) => {
  // Data states
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);           // All activities (including expired) for viewing
  const [activitiesForEnrollment, setActivitiesForEnrollment] = useState([]); // Only open activities for enrollment
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI states
  const [activeTab, setActiveTab] = useState('enrollments'); // enrollments, activities, reports
  const [activityTypeFilter, setActivityTypeFilter] = useState('all'); // all, AfterSchoolSport, SchoolActivity
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showExpiredActivities, setShowExpiredActivities] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all',
    activity: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [categoriesData, allActivitiesData, openActivitiesData, enrollmentsData, studentsData] = await Promise.all([
        apiCall('/activities/categories'),
        apiCall(`/activities?academicYear=${selectedYear}&includeExpired=true`), // All activities for viewing
        apiCall(`/activities?academicYear=${selectedYear}`), // Only open activities (expired filtered by default)
        apiCall(`/activities/enrollments?academicYear=${selectedYear}`),
        apiCall(`/students?academicYear=${selectedYear}`)
      ]);
      
      setCategories(categoriesData);
      setActivities(allActivitiesData);
      setActivitiesForEnrollment(openActivitiesData);
      setEnrollments(enrollmentsData);
      setStudents(studentsData);
    } catch (error) {
      showError('Error loading activities data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, selectedYear, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check if an activity is expired
  const isActivityExpired = (activity) => {
    if (!activity.EndDate) return false;
    const endDate = new Date(activity.EndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  // Filtered data
  const filteredCategories = useMemo(() => {
    if (activityTypeFilter === 'all') return categories;
    return categories.filter(c => c.ActivityType === activityTypeFilter);
  }, [categories, activityTypeFilter]);

  // For the Activities tab - show expired based on toggle
  const filteredActivities = useMemo(() => {
    let result = activities;
    if (activityTypeFilter !== 'all') {
      result = result.filter(a => a.ActivityType === activityTypeFilter);
    }
    // Filter out expired unless showExpiredActivities is true
    if (!showExpiredActivities) {
      result = result.filter(a => !isActivityExpired(a));
    }
    return result;
  }, [activities, activityTypeFilter, showExpiredActivities]);

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter(enrollment => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          enrollment.FirstName?.toLowerCase().includes(searchLower) ||
          enrollment.LastName?.toLowerCase().includes(searchLower) ||
          enrollment.FatherName?.toLowerCase().includes(searchLower) ||
          enrollment.ActivityName?.toLowerCase().includes(searchLower) ||
          enrollment.CategoryName?.toLowerCase().includes(searchLower) ||
          enrollment.StudentID?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Activity type filter
      if (activityTypeFilter !== 'all' && enrollment.ActivityType !== activityTypeFilter) {
        return false;
      }

      // Status filter
      if (filters.status !== 'all' && enrollment.Status !== filters.status) {
        return false;
      }

      // Category filter
      if (filters.category !== 'all' && enrollment.CategoryID !== parseInt(filters.category)) {
        return false;
      }

      // Activity filter
      if (filters.activity !== 'all' && enrollment.ActivityID !== parseInt(filters.activity)) {
        return false;
      }

      return true;
    });
  }, [enrollments, filters, activityTypeFilter]);

  // Group activities by category for display
  const activitiesByCategory = useMemo(() => {
    const grouped = {};
    filteredActivities.forEach(activity => {
      const catId = activity.CategoryID;
      if (!grouped[catId]) {
        grouped[catId] = {
          category: categories.find(c => c.CategoryID === catId),
          activities: []
        };
      }
      grouped[catId].activities.push(activity);
    });
    return Object.values(grouped);
  }, [filteredActivities, categories]);

  // Count expired activities
  const expiredCount = useMemo(() => {
    return activities.filter(a => isActivityExpired(a)).length;
  }, [activities]);

  // Stats
  const stats = useMemo(() => {
    const sportEnrollments = enrollments.filter(e => e.ActivityType === 'AfterSchoolSport' && e.Status === 'Active');
    const schoolEnrollments = enrollments.filter(e => e.ActivityType === 'SchoolActivity' && e.Status === 'Active');
    const totalRevenue = enrollments.filter(e => e.Status === 'Active').reduce((sum, e) => sum + (e.FinalCost || 0), 0);
    const totalCollected = enrollments.filter(e => e.Status === 'Active').reduce((sum, e) => sum + (e.AmountPaid || 0), 0);
    
    return {
      sportEnrollments: sportEnrollments.length,
      schoolEnrollments: schoolEnrollments.length,
      totalRevenue,
      totalCollected,
      outstanding: totalRevenue - totalCollected
    };
  }, [enrollments]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Handle enrollment
  const handleEnroll = async (enrollmentData) => {
    try {
      await apiCall('/activities/enroll', {
        method: 'POST',
        body: JSON.stringify(enrollmentData)
      });
      showSuccess('Student enrolled successfully');
      setShowEnrollModal(false);
      setSelectedActivity(null);
      loadData();
    } catch (error) {
      showError('Error enrolling student: ' + error.message);
    }
  };

  // Handle create activity
  const handleCreateActivity = async (activityData) => {
    try {
      await apiCall('/activities', {
        method: 'POST',
        body: JSON.stringify({ ...activityData, academicYear: selectedYear })
      });
      showSuccess('Activity created successfully');
      setShowActivityModal(false);
      loadData();
    } catch (error) {
      showError('Error creating activity: ' + error.message);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      category: 'all',
      activity: 'all'
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '' && v !== 'all').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
          <p className="text-gray-600">Manage afterschool sports and school activities</p>
        </div>
        <div className="flex gap-2">
          {/* New Activity - GlobalAdmin only */}
<Gate permission="activities.manage_activities.create" label="New Activity">
  <button
    onClick={() => setShowActivityModal(true)}
    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
  >
    <Settings className="w-4 h-4" />
    New Activity
  </button>
</Gate>

{/* Enroll Student */}
<Gate permission="activities.enrollments.create" label="Enroll Student">
  <button
    onClick={() => setShowEnrollModal(true)}
    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
  >
    <Plus className="w-4 h-4" />
    Enroll Student
  </button>
</Gate>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Trophy className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Sports Enrollments</p>
              <p className="text-xl font-bold">{stats.sportEnrollments}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">School Activities</p>
              <p className="text-xl font-bold">{stats.schoolEnrollments}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Collected</p>
              <p className="text-xl font-bold">${stats.totalCollected.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-xl font-bold">${stats.outstanding.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Toggle */}
      <div className="bg-white rounded-lg border p-1 inline-flex">
        <button
          onClick={() => setActivityTypeFilter('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activityTypeFilter === 'all'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          All Activities
        </button>
        <button
          onClick={() => setActivityTypeFilter('AfterSchoolSport')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activityTypeFilter === 'AfterSchoolSport'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Trophy className="w-4 h-4 inline mr-1" />
          Afterschool Sports
        </button>
        <button
          onClick={() => setActivityTypeFilter('SchoolActivity')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activityTypeFilter === 'SchoolActivity'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-1" />
          School Activities
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('enrollments')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'enrollments'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Enrollments ({filteredEnrollments.length})
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'activities'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Activities ({filteredActivities.length})
          </button>
<Gate permission="activities.reports.view" label="Reports tab">
  <button
    onClick={() => setActiveTab('reports')}
    className={`py-4 px-1 border-b-2 font-medium text-sm ${
      activeTab === 'reports'
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <FileText className="w-4 h-4 inline mr-2" />
    Reports
  </button>
</Gate>
        </nav>
      </div>

      {/* Enrollments Tab */}
      {activeTab === 'enrollments' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by student, family, or activity..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border rounded-lg flex items-center gap-2 ${
                activeFilterCount > 0 ? 'border-blue-500 bg-blue-50 text-blue-600' : ''
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Filters</h3>
                <button onClick={clearFilters} className="text-sm text-blue-600 hover:underline">
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value, activity: 'all' }))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="all">All Categories</option>
                    {filteredCategories.map(cat => (
                      <option key={cat.CategoryID} value={cat.CategoryID}>
                        {cat.CategoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
                  <select
                    value={filters.activity}
                    onChange={(e) => setFilters(prev => ({ ...prev, activity: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="all">All Activities</option>
                    {filteredActivities
                      .filter(a => filters.category === 'all' || a.CategoryID === parseInt(filters.category))
                      .map(act => (
                        <option key={act.ActivityID} value={act.ActivityID}>
                          {act.ActivityName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Enrollments Table */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEnrollments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No enrollments found
                    </td>
                  </tr>
                ) : (
                  filteredEnrollments.map(enrollment => (
                    <tr key={enrollment.EnrollmentID} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {enrollment.FirstName} {enrollment.LastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {enrollment.Grade} • {enrollment.FatherName}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{enrollment.ActivityName}</td>
                      <td className="px-4 py-3 text-sm">{enrollment.CategoryName}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          enrollment.ActivityType === 'AfterSchoolSport'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {enrollment.ActivityType === 'AfterSchoolSport' ? 'Sport' : 'Activity'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">${enrollment.FinalCost?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">${enrollment.AmountPaid?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={enrollment.BalanceRemaining > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                          ${enrollment.BalanceRemaining?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          enrollment.Status === 'Active' ? 'bg-green-100 text-green-800' :
                          enrollment.Status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {enrollment.Status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div className="space-y-4">
          {/* Show expired toggle */}
          {expiredCount > 0 && (
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showExpiredActivities}
                  onChange={(e) => setShowExpiredActivities(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Show expired activities ({expiredCount})
                </span>
              </label>
            </div>
          )}

          {activitiesByCategory.length === 0 ? (
            <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
              No activities found for {selectedYear}
            </div>
          ) : (
            activitiesByCategory.map(({ category, activities: catActivities }) => (
              <div key={category?.CategoryID} className="bg-white rounded-lg border overflow-hidden">
                <button
                  onClick={() => toggleCategory(category?.CategoryID)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    {expandedCategories[category?.CategoryID] ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                    <span className="font-medium">{category?.CategoryName}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      category?.ActivityType === 'AfterSchoolSport'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {category?.ActivityType === 'AfterSchoolSport' ? 'Sport' : 'Activity'}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">{catActivities.length} activities</span>
                </button>
                
                {expandedCategories[category?.CategoryID] && (
                  <div className="divide-y">
                    {catActivities.map(activity => {
                      const expired = isActivityExpired(activity);
                      return (
                        <div 
                          key={activity.ActivityID} 
                          className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 ${expired ? 'opacity-60 bg-gray-50' : ''}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{activity.ActivityName}</span>
                              {expired && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Expired
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {activity.PaymentSchedule} • ${activity.PriceUSD}
                              {activity.MaxCapacity && ` • Capacity: ${activity.MaxCapacity}`}
                              {activity.EndDate && (
                                <span className="ml-2">
                                  • Ends: {new Date(activity.EndDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="font-medium">{activity.EnrolledCount || 0} enrolled</div>
                              {activity.SpotsRemaining !== null && (
                                <div className="text-sm text-gray-500">
                                  {activity.SpotsRemaining} spots left
                                </div>
                              )}
                            </div>
{!expired && (
  <Gate permission="activities.enrollments.create" label="Enroll (activity card)">
  <button
    onClick={() => {
      setSelectedActivity(activity);
      setShowEnrollModal(true);
    }}
    className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
  >
    Enroll
  </button>
  </Gate>
)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Total Enrollments</p>
              <p className="text-2xl font-bold">{enrollments.filter(e => e.Status === 'Active').length}</p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold">
                ${enrollments.filter(e => e.Status === 'Active').reduce((sum, e) => sum + (e.FinalCost || 0), 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Total Collected</p>
              <p className="text-2xl font-bold text-green-600">
                ${enrollments.filter(e => e.Status === 'Active').reduce((sum, e) => sum + (e.AmountPaid || 0), 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-2xl font-bold text-red-600">
                ${enrollments.filter(e => e.Status === 'Active').reduce((sum, e) => sum + (e.BalanceRemaining || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Report Options */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4">Generate Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Student List Report */}
              <div 
                onClick={() => setShowReportModal(true)}
                className="border rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <h4 className="font-medium">Student Enrollment List</h4>
                </div>
                <p className="text-sm text-gray-600">
                  View and print list of students enrolled in each activity with financial details
                </p>
              </div>

              {/* Revenue by Category */}
              <div 
                onClick={() => setShowReportModal(true)}
                className="border rounded-lg p-4 hover:border-green-500 hover:bg-green-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <h4 className="font-medium">Financial Report</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Revenue, collections, and outstanding balances by activity or category
                </p>
              </div>

              {/* Activity Summary */}
              <div 
                onClick={() => setShowReportModal(true)}
                className="border rounded-lg p-4 hover:border-purple-500 hover:bg-purple-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <h4 className="font-medium">Activity Summary</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Overview of all activities with enrollment counts and capacity
                </p>
              </div>
            </div>
          </div>

          {/* Quick Summary Tables */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Category */}
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-medium mb-4">Summary by Category</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Category</th>
                    <th className="text-right py-2">Students</th>
                    <th className="text-right py-2">Revenue</th>
                    <th className="text-right py-2">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => {
                    const catEnrollments = enrollments.filter(
                      e => e.CategoryID === cat.CategoryID && e.Status === 'Active'
                    );
                    const revenue = catEnrollments.reduce((sum, e) => sum + (e.FinalCost || 0), 0);
                    const outstanding = catEnrollments.reduce((sum, e) => sum + (e.BalanceRemaining || 0), 0);
                    
                    if (catEnrollments.length === 0) return null;
                    
                    return (
                      <tr key={cat.CategoryID} className="border-b hover:bg-gray-50">
                        <td className="py-2">{cat.CategoryName}</td>
                        <td className="py-2 text-right">{catEnrollments.length}</td>
                        <td className="py-2 text-right">${revenue.toFixed(2)}</td>
                        <td className={`py-2 text-right ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ${outstanding.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Top Activities */}
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-medium mb-4">Top Activities by Enrollment</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Activity</th>
                    <th className="text-right py-2">Students</th>
                    <th className="text-right py-2">Capacity</th>
                  </tr>
                </thead>
                <tbody>
                  {activities
                    .filter(a => (a.EnrolledCount || 0) > 0)
                    .sort((a, b) => (b.EnrolledCount || 0) - (a.EnrolledCount || 0))
                    .slice(0, 8)
                    .map(activity => (
                      <tr key={activity.ActivityID} className="border-b hover:bg-gray-50">
                        <td className="py-2">
                          <div>{activity.ActivityName}</div>
                          <div className="text-xs text-gray-500">{activity.CategoryName}</div>
                        </td>
                        <td className="py-2 text-right font-medium">{activity.EnrolledCount || 0}</td>
                        <td className="py-2 text-right text-gray-500">
                          {activity.MaxCapacity ? `/ ${activity.MaxCapacity}` : '∞'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Modal - Pass only OPEN activities (not expired) */}
      {showEnrollModal && (
        <ActivityEnrollModal
          isOpen={showEnrollModal}
          onClose={() => {
            setShowEnrollModal(false);
            setSelectedActivity(null);
          }}
          onSubmit={handleEnroll}
          activities={activitiesForEnrollment}  // Only activities without expired EndDate
          categories={filteredCategories}
          students={students}
          enrollments={enrollments}
          selectedActivity={selectedActivity}
          selectedYear={selectedYear}
        />
      )}

      {/* Activity Modal */}
      {showActivityModal && (
        <ActivityModal
          isOpen={showActivityModal}
          onClose={() => setShowActivityModal(false)}
          onSubmit={handleCreateActivity}
          categories={categories}
          selectedYear={selectedYear}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ActivityReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          activities={activities}
          enrollments={enrollments}
          categories={categories}
          selectedYear={selectedYear}
        />
      )}
    </div>
  );
};

export default Activities;